import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from "@vercel/analytics/react";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import OnboardingRings from './pages/OnboardingRings';
import FigChat from './pages/FigChat';
import ConnectEmail from './pages/ConnectEmail';
import DataPrivacyPage from './pages/DataPrivacyPage';
import RoadmapPage from './pages/RoadmapPage';
import RoleDetailPage from './pages/RoleDetailPage';
import SavedRoadmapsPage from './pages/SavedRoadmapsPage';
import DiscoverRolesPage from './pages/DiscoverRolesPage';
// import GlobalJobSearchPage from './pages/GlobalJobSearchPage'; // Global Jobs feature disabled
import FeedPage from './pages/FeedPage';
import SpillTeaPage from './pages/SpillTeaPage';
import SpillTeaReviewPage from './pages/SpillTeaReviewPage';
import SpillTeaHomePage from './pages/SpillTeaHomePage';
import SpillTeaCompanyPage from './pages/SpillTeaCompanyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import LandingPage from './pages/LandingPage';
import CoachMemoryPage from './pages/CoachMemoryPage';
import SuperhumanDashboard from './pages/SuperhumanDashboard';
import TsentaDashboard from './pages/TsentaDashboard';
import LandeedDashboard from './pages/LandeedDashboard';
import SorceDashboard from './pages/SorceDashboard';
import WritingPage from './pages/WritingPage';
import WriterGate from './pages/WriterGate';
import PaywallPage from './pages/PaywallPage';
import './App.css';

// Error Boundary to catch React errors and prevent complete freeze
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen p-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
          <p className="text-gray-600 mb-4">We're sorry, but something unexpected happened.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Refresh & Start Over
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected Route Component - ensures user is logged in
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/connect-email" replace />;
  }

  return children;
};

// Paywall Route Component - ensures user has paid for lifetime access
const PaywallRoute = ({ children }) => {
  const { user, hasLifetimeAccess, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/connect-email" replace />;
  }

  if (!hasLifetimeAccess) {
    return <Navigate to="/paywall" replace />;
  }

  return children;
};

const UploadAdOverlay = () => {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const showAd = () => setVisible(true);
    const hideAd = () => setVisible(false);

    window.addEventListener('fig:show-upload-ad', showAd);
    window.addEventListener('fig:hide-upload-ad', hideAd);

    return () => {
      window.removeEventListener('fig:show-upload-ad', showAd);
      window.removeEventListener('fig:hide-upload-ad', hideAd);
    };
  }, []);

  React.useEffect(() => {
    if (!visible) return;

    const renderAd = () => {
      if (window.__EnokiNetwork?.render) {
        window.__EnokiNetwork.render({
          placementId: 'upload_button',
          container: '#np-upload_button',
          onEvent: (event) => {
            if (event?.type === 'close') {
              setVisible(false);
            }
          }
        });
        return;
      }

      window.__EnokiNetwork = window.__EnokiNetwork || { q: [] };
      window.__EnokiNetwork.q = window.__EnokiNetwork.q || [];
      window.__EnokiNetwork.q.push(function () {
        window.__EnokiNetwork.render({
          placementId: 'upload_button',
          container: '#np-upload_button',
          onEvent: (event) => {
            if (event?.type === 'close') {
              setVisible(false);
            }
          }
        });
      });
    };

    window.__EnokiNetwork = window.__EnokiNetwork || { q: [] };

    if (!document.querySelector('script[data-enoki-loader="47da81"]')) {
      const script = document.createElement('script');
      script.src = '/47da81/loader.js';
      script.async = true;
      script.dataset.enokiLoader = '47da81';
      document.head.appendChild(script);
    }

    renderAd();
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 top-[58%] z-[60] flex -translate-y-1/2 justify-center px-4 pointer-events-none">
      <div className="mx-auto flex w-full max-w-md justify-center pointer-events-auto">
        <div id="np-upload_button" className="flex w-full justify-center" />
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App min-h-screen">
          <Analytics />
          <BrowserRouter>
          <Routes>
            {/* Root path shows public landing page */}
            <Route path="/" element={<LandingPage />} />
            {/* Public analytics dashboards */}
            <Route path="/superhuman" element={<SuperhumanDashboard />} />
            <Route path="/tsenta" element={<TsentaDashboard />} />
            <Route path="/landeed" element={<LandeedDashboard />} />
            <Route path="/sorce" element={<SorceDashboard />} />
            <Route path="/onboarding" element={<OnboardingRings />} />
            <Route path="/connect-email" element={<ConnectEmail />} />
            <Route path="/data-privacy" element={<DataPrivacyPage />} />
            <Route path="/fig-chat" element={<FigChat />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/paywall" element={
              <ProtectedRoute>
                <PaywallPage />
              </ProtectedRoute>
            } />

            {/* Roadmap - free to view, paywall enforced inline for interactions */}
            <Route path="/roadmap" element={
              <ProtectedRoute>
                <RoadmapPage />
              </ProtectedRoute>
            } />
            <Route path="/roadmap/:roadmapId" element={
              <ProtectedRoute>
                <RoadmapPage />
              </ProtectedRoute>
            } />
            <Route path="/roadmap/:roadmapId/roles/:roleId" element={
              <PaywallRoute>
                <RoleDetailPage />
              </PaywallRoute>
            } />
            <Route path="/coach/messages" element={
              <ProtectedRoute>
                <CoachMemoryPage />
              </ProtectedRoute>
            } />
            <Route path="/roles/:roleId" element={
              <PaywallRoute>
                <RoleDetailPage />
              </PaywallRoute>
            } />
            <Route path="/saved" element={
              <PaywallRoute>
                <SavedRoadmapsPage />
              </PaywallRoute>
            } />
            <Route path="/discover" element={
              <PaywallRoute>
                <DiscoverRolesPage />
              </PaywallRoute>
            } />
            {/* Global Jobs feature disabled
            <Route path="/global-jobs" element={
              <PaywallRoute>
                <GlobalJobSearchPage />
              </PaywallRoute>
            } />
            */}
            <Route path="/feed" element={
              <PaywallRoute>
                <FeedPage />
              </PaywallRoute>
            } />
            <Route path="/writing" element={
              <PaywallRoute>
                <WritingPage />
              </PaywallRoute>
            } />
            {/* Direct access route — no onboarding required, resume gate built-in */}
            <Route path="/writer" element={
              <WriterGate>
                <WritingPage />
              </WriterGate>
            } />
            <Route path="/spill-tea" element={
              <PaywallRoute>
                <SpillTeaPage />
              </PaywallRoute>
            } />
            <Route path="/spill-tea/home" element={
              <PaywallRoute>
                <SpillTeaHomePage />
              </PaywallRoute>
            } />
            <Route path="/spill-tea/reviews/:companyDomain" element={
              <PaywallRoute>
                <SpillTeaCompanyPage />
              </PaywallRoute>
            } />
            <Route path="/spill-tea/:companyName" element={
              <PaywallRoute>
                <SpillTeaReviewPage />
              </PaywallRoute>
            } />


{/* Handle typo URL since user keeps hitting it */}
            <Route path="/email-connect" element={<Navigate to="/connect-email" replace />} />

            {/* Catch all - redirect to root */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <UploadAdOverlay />
        </BrowserRouter>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
